import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Typography,
} from "@mui/material";
import type { Deal, User } from "../../../types/deal";
import type { Nda, NdaAppliesTo, NdaRestrictionType } from "../../../types/nda";
import {
  NDA_APPLIES_TO_OPTIONS,
  isNdaAppliesTo,
} from "../../../types/nda";
import { createNda, getNda, updateNda } from "../../../services/ndasApi";
import { apiGetById, apiPut } from "../../../services/genesisApi/httpsMethods";
import { CustomButton } from "../../../components/common/customButton";
import { CustomTextInput } from "../../../components/common/inputFields/customTextInput";
import { CustomSingleSelect } from "../../../components/common/inputFields/customSingleSelect";
import { useCurrentUser } from "@/context/CurrentUserContext";
import { useDealsProvider } from "@/context/DealsContext";
import { useReferenceLists } from "../../../context/GlobalGetLists";
import "../../../assets/css/Text-General.css";

const RESTRICTION_TYPE_OPTIONS = [
  { id: "non_circumvent", name: "Non-circumvent" },
  { id: "no_contact", name: "No contact" },
];

type NdaForm = {
  applies_to: NdaAppliesTo | "";
  person_responsible_id: number | "";
  restriction_type: NdaRestrictionType | "";
  summary: string;
  expiry_date: string;
};

function emptyNdaForm(): NdaForm {
  return {
    applies_to: "",
    person_responsible_id: "",
    restriction_type: "non_circumvent",
    summary: "",
    expiry_date: "",
  };
}

function responsibleIdFromNda(nda: Partial<Nda> | null | undefined): number | "" {
  if (!nda) return "";
  if (typeof nda.person_responsible_id === "number") {
    return nda.person_responsible_id;
  }
  const nested = nda.person_responsible;
  if (nested && typeof nested === "object" && typeof nested.id === "number") {
    return nested.id;
  }
  return "";
}

function formFromNda(nda: Partial<Nda> | null | undefined): NdaForm {
  if (!nda) return emptyNdaForm();
  const type = nda.restriction_type;
  const applies = nda.applies_to;
  return {
    applies_to: isNdaAppliesTo(applies) ? applies : "",
    person_responsible_id: responsibleIdFromNda(nda),
    restriction_type:
      type === "non_circumvent" || type === "no_contact" ? type : "",
    summary: nda.summary ?? "",
    expiry_date: nda.expiry_date ? String(nda.expiry_date).slice(0, 10) : "",
  };
}

type NdaFormSnapshot = {
  hasRestriction: boolean;
  ndaId: number | null;
  form: NdaForm;
};

function snapshotsEqual(a: NdaFormSnapshot, b: NdaFormSnapshot): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export interface DealNdaTabProps {
  deal: Deal | null;
  onDealUpdated?: (deal: Deal) => void;
}

export default function DealNdaTab({ deal, onDealUpdated }: DealNdaTabProps) {
  const { canEdit } = useCurrentUser();
  const { users } = useReferenceLists();
  const { setDeals, setInitialDeals } = useDealsProvider();
  const dealId = typeof deal?.id === "number" ? deal.id : null;

  const [hasRestriction, setHasRestriction] = useState(false);
  const [ndaId, setNdaId] = useState<number | null>(null);
  const [form, setForm] = useState<NdaForm>(emptyNdaForm());
  const [baseline, setBaseline] = useState<NdaFormSnapshot | null>(null);
  const [isDraft, setIsDraft] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const publishDealUpdate = useCallback(
    (updated: Deal) => {
      onDealUpdated?.(updated);
      const patchList = (prev: Deal[]) =>
        prev.map((row) =>
          row.id === updated.id
            ? {
                ...row,
                nda_id: updated.nda_id ?? null,
                nda: updated.nda ?? null,
              }
            : row,
        );
      setDeals(patchList);
      setInitialDeals(patchList);
    },
    [onDealUpdated, setDeals, setInitialDeals],
  );

  const applyLoadedState = useCallback((nda: Nda | null | undefined) => {
    const id = typeof nda?.id === "number" ? nda.id : null;
    const nextForm = formFromNda(nda);
    const nextHas = id != null || (nda != null && Object.keys(nda).length > 0);
    setNdaId(id);
    setForm(nextForm);
    setHasRestriction(nextHas);
    setIsDraft(nda != null && id == null);
    setBaseline({
      hasRestriction: nextHas,
      ndaId: id,
      form: nextForm,
    });
  }, []);

  useEffect(() => {
    if (!deal || dealId == null) return;

    setError(null);
    setSuccess(null);

    let cancelled = false;
    setLoading(true);

    const load = async () => {
      const linkedId =
        deal.nda_id ??
        (typeof deal.nda?.id === "number" ? deal.nda.id : null);

      if (linkedId != null) {
        const detail = await getNda(linkedId);
        if (cancelled) return;
        if (detail.success && detail.data) {
          applyLoadedState(detail.data);
          return;
        }
        if (deal.nda) {
          applyLoadedState(deal.nda);
          return;
        }
      }

      // AI extraction draft: nested nda without id
      if (deal.nda && typeof deal.nda.id !== "number") {
        applyLoadedState(deal.nda);
        return;
      }

      applyLoadedState(null);
    };

    void load().finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [deal, dealId, applyLoadedState]);

  const currentSnapshot: NdaFormSnapshot = {
    hasRestriction,
    ndaId,
    form,
  };

  const hasChanges =
    baseline != null && !snapshotsEqual(currentSnapshot, baseline);

  const updateField = <K extends keyof NdaForm>(key: K, value: NdaForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSuccess(null);
  };

  const handleToggleRestriction = (checked: boolean) => {
    setHasRestriction(checked);
    setSuccess(null);
    if (checked && !form.restriction_type) {
      setForm((prev) => ({ ...prev, restriction_type: "non_circumvent" }));
    }
  };

  const handleSave = async () => {
    if (dealId == null) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      if (!hasRestriction) {
        const dealResponse = await apiPut("deal", dealId, {
          nda_id: null,
          nda: null,
        });
        if (!dealResponse.success) {
          setError(
            typeof dealResponse.message === "string"
              ? dealResponse.message
              : "Failed to unlink NDA",
          );
          return;
        }
        const refreshed = await apiGetById<Deal[]>("deal", dealId);
        if (refreshed.success && refreshed.data?.[0]) {
          publishDealUpdate(refreshed.data[0]);
        } else {
          applyLoadedState(null);
          publishDealUpdate({
            ...(deal as Deal),
            id: dealId,
            nda_id: null,
            nda: null,
          });
        }
        setSuccess("Saved");
        return;
      }

      const personId =
        form.person_responsible_id === ""
          ? null
          : Number(form.person_responsible_id);

      const payload = {
        person_responsible_id: personId,
        person_responsible: personId != null ? { id: personId } : null,
        restriction_type: form.restriction_type || "non_circumvent",
        applies_to: form.applies_to || null,
        summary: form.summary.trim() || null,
        expiry_date: form.expiry_date || null,
      };

      let saved: Nda | null = null;

      if (ndaId == null || isDraft) {
        const created = await createNda({
          ...payload,
          deal_id: dealId,
        });
        if (!created.success || !created.data) {
          setError(
            typeof created.message === "string"
              ? created.message
              : "Failed to create NDA",
          );
          return;
        }
        saved = created.data;
      } else {
        const updated = await updateNda(ndaId, payload);
        if (!updated.success || !updated.data) {
          setError(
            typeof updated.message === "string"
              ? updated.message
              : "Failed to update NDA",
          );
          return;
        }
        saved = updated.data;
      }

      const savedId = typeof saved.id === "number" ? saved.id : null;
      if (savedId == null) {
        setError("NDA saved but no id returned");
        return;
      }

      const refreshed = await apiGetById<Deal[]>("deal", dealId);
      if (refreshed.success && refreshed.data?.[0]) {
        publishDealUpdate(refreshed.data[0]);
      } else {
        applyLoadedState(saved);
        publishDealUpdate({
          ...(deal as Deal),
          id: dealId,
          nda_id: savedId,
          nda: saved,
        });
      }

      setSuccess("Saved");
    } catch {
      setError("Failed to save NDA details");
    } finally {
      setSaving(false);
    }
  };

  const disabled = !canEdit || saving || loading;
  const fieldsDisabled = disabled || !hasRestriction;
  const usersList: User[] = Array.isArray(users) ? users : [];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        maxWidth: 640,
      }}
    >
      <Typography
        className="text-general-page-header"
        sx={{
          "&&": {
            height: "auto !important",
            mb: "0 !important",
            paddingBottom: "0 !important",
          },
        }}
      >
        NDA
      </Typography>
      <Typography
        className="text-general-page-subheader"
        sx={{
          "&&": {
            height: "auto !important",
            mb: 1,
            paddingBottom: "0 !important",
          },
        }}
      >
        Non Circumvent / No Contact Restriction
      </Typography>

      {isDraft && (
        <Typography variant="body2" sx={{ color: "#B45309" }}>
          Draft from extraction — confirm details and Save to create the NDA
          and link it to this deal.
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Checkbox
          disableRipple
          checked={hasRestriction}
          onChange={(e) => handleToggleRestriction(e.target.checked)}
          disabled={disabled}
          sx={{ color: "#1B7A63 !important", p: 0 }}
        />
        <Typography sx={{ lineHeight: 1.2 }}>Has restriction</Typography>
      </Box>

      <CustomSingleSelect
        value={form.restriction_type}
        onChange={(e) =>
          updateField(
            "restriction_type",
            (e.target.value || "") as NdaRestrictionType | "",
          )
        }
        options={RESTRICTION_TYPE_OPTIONS}
        labelKey="name"
        placeholder="Restriction type"
        fullWidth
        disabled={fieldsDisabled}
      />

      <CustomTextInput
        label="Expiry date"
        type="date"
        value={form.expiry_date}
        onChange={(v) => updateField("expiry_date", String(v))}
        disabled={fieldsDisabled}
        fullWidth
      />

      <CustomSingleSelect
        value={form.applies_to}
        onChange={(e) =>
          updateField(
            "applies_to",
            (e.target.value || "") as NdaAppliesTo | "",
          )
        }
        options={NDA_APPLIES_TO_OPTIONS}
        labelKey="name"
        placeholder="Applies to"
        fullWidth
        disabled={fieldsDisabled}
      />

      <CustomSingleSelect
        value={form.person_responsible_id}
        onChange={(e) => {
          const raw = e.target.value;
          updateField(
            "person_responsible_id",
            raw === "" || raw == null ? "" : Number(raw),
          );
        }}
        options={usersList}
        labelKey="name"
        placeholder="Person responsible"
        fullWidth
        disabled={fieldsDisabled}
      />

      <CustomTextInput
        label="Summary"
        value={form.summary}
        onChange={(v) => updateField("summary", String(v))}
        disabled={fieldsDisabled}
        multiline
        minRows={16}
        fullWidth
      />

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
      {success && (
        <Typography variant="body2" sx={{ color: "#1B7A63" }}>
          {success}
        </Typography>
      )}

      {canEdit && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
          <CustomButton
            text="Save"
            handleButtonPress={() => void handleSave()}
            isButtonDisabled={saving || dealId == null || !hasChanges}
            isLoading={saving}
            solid
          />
        </Box>
      )}
    </Box>
  );
}
